import React, { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";

function MembersArea() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  // Check for valid token on mount
  useEffect(() => {
    const token = localStorage.getItem('member_token');
    const memberData = localStorage.getItem('member_data');
    const tokenExpiry = localStorage.getItem('token_expiry');
    
    if (token && memberData && tokenExpiry) {
      const now = Date.now();
      const expiryTime = parseInt(tokenExpiry, 10);
      
      if (now < expiryTime) {
        // Token is still valid, redirect to dashboard
        navigate('/members/members-dashboard', { replace: true });
      } else {
        // Token expired, clear storage
        localStorage.removeItem('member_token');
        localStorage.removeItem('member_data');
        localStorage.removeItem('token_expiry');
      }
    }
  }, [navigate]);

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!loginForm.email || !loginForm.password) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please enter both email and password.',
        timer: 3000,
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Clear any existing authorization header
      delete axios.defaults.headers.common['Authorization'];
      
      const response = await axios.post('https://backend.dlapakistan.com/api/members/login/', {
        email: loginForm.email,
        password: loginForm.password
      });
      
      if (response.data.success) {
        const { access, member: memberData, expires_in } = response.data;
        
        // Calculate expiry time (45 minutes from now)
        const expiryTime = Date.now() + (expires_in * 1000);
        
        // Store token, member data, and expiry time
        localStorage.setItem('member_token', access);
        localStorage.setItem('member_data', JSON.stringify(memberData));
        localStorage.setItem('token_expiry', expiryTime.toString());
        
        // Set axios default header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        
        Swal.fire({
          icon: 'success',
          title: 'Login Successful',
          text: 'Welcome to your dashboard! Your session will expire in 45 minutes.',
          timer: 3000,
          showConfirmButton: false
        });
        
        // Redirect to dashboard
        navigate('/members/members-dashboard');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Login Failed',
          text: response.data.error || 'Invalid credentials',
          timer: 3000,
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Invalid email or password';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = 'Invalid credentials';
      } else if (error.response?.status === 400) {
        errorMessage = 'Please check your email and password';
      } else if (!error.response) {
        errorMessage = 'Network error. Please try again.';
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: errorMessage,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = () => {
    Swal.fire({
      title: 'Reset Password',
      html: `
        <p>Please contact the administrator to reset your password.</p>
        <p class="text-gray-600 text-sm mt-2">
          Email: admin@example.com<br>
          Phone: +1 234 567 8900
        </p>
      `,
      icon: 'info',
      confirmButtonText: 'OK',
    });
  };

  return (
    <div className="min-h-screen bg-white mt-40 px-4 py-16">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-normal text-gray-800 border-b border-[#7db63c] pb-3 mb-10">
          Members Area
        </h2>

        <div className="bg-[#f2f4f7] p-8 rounded-md">
          <h3 className="text-xl font-medium text-gray-800 mb-6">
            Member Login
          </h3>
          
          <Form onSubmit={handleLoginSubmit}>
            <Form.Group className="mb-4">
              <Form.Label className="text-gray-800">
                Email Address <span className="text-red-600">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={loginForm.email}
                onChange={handleLoginChange}
                className="py-2 px-3 rounded border border-gray-400 focus:ring-1 focus:ring-[#7db63c] focus:border-[#7db63c]"
                required
                disabled={loading}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="text-gray-800">
                Password <span className="text-red-600">*</span>
              </Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={loginForm.password}
                onChange={handleLoginChange}
                className="py-2 px-3 rounded border border-gray-400 focus:ring-1 focus:ring-[#7db63c] focus:border-[#7db63c]"
                required
                disabled={loading}
              />
            </Form.Group>

            <div className="d-flex align-items-center gap-3 mb-3">
              <Button
                type="submit"
                className="bg-[#7db63c] border-0 px-6 py-2 hover:bg-[#6a9e32] transition"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Log in'}
              </Button>

              <Form.Check
                type="checkbox"
                label="Remember me"
                className="text-gray-700"
              />
            </div>

            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-[#7db63c] text-sm hover:underline bg-transparent border-0 p-0 cursor-pointer"
            >
              Lost your password?
            </button>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-blue-800 text-sm mb-0">
                <strong>Note:</strong> For security, your session will automatically expire after 45 minutes of inactivity.
              </p>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default MembersArea;